import CloseIcon from '@mui/icons-material/Close';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import MinimizeIcon from '@mui/icons-material/Minimize';
import { IconButton } from '@mui/material';
import { motion, useDragControls } from 'framer-motion';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { useAppState } from '../../Context/AppStateContext';
import { theme } from '../../theme';

interface ContainerProps {
  zIndex: number;
}

interface BoxProps {
  $color?: string;
  $width: number;
  $isMaximized?: boolean;
}

interface AlertHeaderProps {
  $color?: string;
}

interface ModalProps {
  widthString: string;
  xPositionPercent: number;
  yPositionPercent: number;
  children: React.ReactNode;
  onClose?: () => void;
  onFocus?: () => void;
  isActive?: boolean;
  color?: string;
  header: string;
  icon?: React.ReactNode;
  startMaximized?: boolean;
}

const Container = styled(motion.div) <ContainerProps>`
  overflow: hidden;
  width: 100dvw;
  height: 100dvh;
  position: absolute;
  pointer-events: none;
  z-index: ${props => props.zIndex};
`;

const Box = styled(motion.div) <BoxProps>`
  border-radius: ${props => (props.$isMaximized ? '0' : '3px')};
  border: ${props =>
    props.$isMaximized ? 'none' : `2px solid ${props.$color || theme.colors.green}`};
  border-top: ${props =>
    props.$isMaximized ? `1px solid ${props.$color || theme.colors.green}` : undefined};
  border-bottom: ${props => (props.$isMaximized ? 'none' : undefined)};
  color: ${props => props.$color || theme.colors.green};
  min-height: 100px;
  max-width: ${props => (props.$isMaximized ? '100vw' : '100vw')};
  background-color: #061308;
  pointer-events: auto;
  width: ${({ $width, $isMaximized }) => ($isMaximized ? '100vw' : `${$width}px`)};
  min-height: 100px;
  box-shadow: ${props =>
    props.$isMaximized
      ? 'none'
      : 'inset 1px 1px 1px rgba(255, 255, 255, 0.1), inset -1px -1px 1px rgba(0, 0, 0, 0.3)'};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    border-radius: ${props => (props.$isMaximized ? '0' : '2px')};
    z-index: 1;
  }

  p {
    color: white;
    font-size: 16px;
  }
`;

const WindowControls = styled.div`
  display: flex;
  align-items: center;
`;

const ControlButton = styled(IconButton)`
  margin: 0 2px !important;
  padding: 2px !important;
  background-color: #000000 !important;
  border: 1px solid ${theme.colors.green} !important;
  border-radius: 0 !important;
  box-shadow:
    inset 1px 1px 0 rgba(255, 255, 255, 0.1),
    inset -1px -1px 0 rgba(0, 0, 0, 0.3) !important;

  &:hover {
    background-color: #0a1a0a !important;
  }

  &.close-button {
    background-color: #000000 !important;
    border-color: #ff6057 !important;

    &:hover {
      background-color: #1a0000 !important;
    }
  }

  svg {
    font-size: 16px !important;
    color: ${theme.colors.green} !important;
  }

  &.close-button svg {
    color: #ff6057 !important;
  }
`;

const AlertHeader = styled.div<AlertHeaderProps>`
  padding: 4px 6px;
  color: black;
  background: linear-gradient(
    to right,
    ${props => props.$color || theme.colors.green},
    ${props => (props.$color ? `${props.$color}ee` : `${theme.colors.green}ee`)} 70%,
    ${props => (props.$color ? `${props.$color}cc` : `${theme.colors.green}cc`)} 100%
  );
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #000;
  text-shadow: 1px 1px 1px rgba(255, 255, 255, 0.3);
  cursor: move;
  user-select: none;
  touch-action: none;

  h2 {
    padding: 0 10px;
    margin: 0;
    font-size: 14px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    color: black;
    pointer-events: none;

    .window-icon {
      margin-right: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

const Content = styled.div<{ $isMaximized?: boolean }>`
  display: flex;
  flex-direction: column;
  max-height: ${props => (props.$isMaximized ? 'calc(100vh - 90px)' : '60vh')};
  height: ${props => (props.$isMaximized ? 'calc(100vh - 90px)' : 'auto')};
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  overflow-y: auto;
  position: relative;

  /* Windows-style scrollbar */
  &::-webkit-scrollbar {
    width: 16px;
    background-color: #061308;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.green};
    border: 1px solid #000;
    border-radius: 0;
  }

  &::-webkit-scrollbar-track {
    background-color: #061308;
    border: 1px solid #333;
  }

  &::-webkit-scrollbar-button {
    background-color: #061308;
    border: 1px solid #333;
    height: 16px;
  }

  &::-webkit-scrollbar-button:vertical:start:decrement {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%231FCF5E' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M18 15l-6-6-6 6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
  }

  &::-webkit-scrollbar-button:vertical:end:increment {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%231FCF5E' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
  }
`;

const Modal: React.FC<ModalProps> = ({
  color,
  header,
  icon,
  widthString,
  xPositionPercent,
  yPositionPercent,
  children,
  onClose,
  onFocus,
  isActive,
  startMaximized = false,
}) => {
  const {
    windowWidth,
    windowHeight,
    addMinimizedWindow,
    removeMinimizedWindow,
    registerWindow,
    unregisterWindow,
    bringToFront,
    minimizedWindows,
  } = useAppState();

  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(startMaximized);
  const [windowId] = useState(uuidv4());
  const [previousPosition, setPreviousPosition] = useState({ x: 0, y: 0 });
  const [zIndex, setZIndex] = useState<number>(100);

  // Add drag controls
  const dragControls = useDragControls();

  // Register this window when it's created and get initial z-index
  useEffect(() => {
    const initialZIndex = registerWindow(windowId);
    setZIndex(initialZIndex);

    // Unregister when component unmounts
    return () => {
      unregisterWindow(windowId);
    };
  }, []);

  const widthInPixels = () => {
    let pixels = 0;

    if (widthString.includes('%') || widthString.includes('vw') || widthString.includes('dvw')) {
      pixels = windowWidth * (parseInt(widthString.replace('%', '')) / 100);
    }
    if (widthString.includes('px')) {
      pixels = parseInt(widthString.replace('px', ''));
    }
    if (pixels > windowWidth) pixels = windowWidth - 20;

    return pixels;
  };

  const [constraints, setConstraints] = useState({
    top: 0,
    left: 0,
    right: windowWidth - widthInPixels(),
    bottom: windowHeight - 50,
  });
  const [position, setPosition] = useState({
    x: windowWidth * (xPositionPercent / 100) - widthInPixels() / 2,
    y: windowHeight * (yPositionPercent / 100) - 50,
  });

  useEffect(() => {
    const newXPosition = windowWidth * (xPositionPercent / 100) - widthInPixels() / 2;
    const newYPosition = windowHeight * (yPositionPercent / 100) - 50;

    if (!isMaximized) {
      setPosition({ x: newXPosition, y: newYPosition });
    }

    setConstraints({
      top: 0,
      left: 0,
      right: windowWidth - widthInPixels(),
      bottom: windowHeight - 50,
    });
  }, [windowWidth, windowHeight, xPositionPercent, yPositionPercent, isMaximized]);

  // Add this function to ensure position stays within bounds
  const ensurePositionWithinBounds = (pos: { x: number; y: number }) => {
    return {
      x: Math.max(0, Math.min(pos.x, windowWidth - widthInPixels())),
      y: Math.max(0, Math.min(pos.y, windowHeight - 50)),
    };
  };

  const handleMinimize = () => {
    setIsMinimized(true);

    // Add to taskbar
    addMinimizedWindow({
      id: windowId,
      header,
      color,
      icon,
      restore: () => {
        setIsMinimized(false);
        removeMinimizedWindow(windowId);
        // Bring to front when restored
        const newZIndex = bringToFront(windowId);
        setZIndex(newZIndex);
      },
    });
  };

  const handleMaximize = () => {
    if (isMaximized) {
      // Restore previous position and size
      setPosition(previousPosition);
      setIsMaximized(false);
    } else {
      // Save current position and size for later restoration
      setPreviousPosition(position);

      // Maximize - set position to exactly 0,0 and let the width/height be handled by styling
      setPosition({ x: 0, y: 0 });
      setIsMaximized(true);
    }

    // Bring to front when maximized/restored
    const newZIndex = bringToFront(windowId);
    setZIndex(newZIndex);
  };

  const handleClose = () => {
    // Always remove from minimized windows if it's there
    if (isMinimized) {
      removeMinimizedWindow(windowId);
    }
    if (onClose) onClose();
  };

  const handleWindowClick = () => {
    // Bring window to front when clicked
    const newZIndex = bringToFront(windowId);
    setZIndex(newZIndex);

    // Call focus handler if provided
    if (onFocus) {
      onFocus();
    }
  };

  if (isMinimized) {
    return null; // Don't render the window if minimized
  }

  return (
    <Container onClick={handleWindowClick} tabIndex={0} zIndex={zIndex}>
      <Box
        $width={isMaximized ? windowWidth : widthInPixels()}
        $color={color}
        $isMaximized={isMaximized}
        drag={!isMaximized}
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={constraints}
        dragElastic={0.1}
        dragMomentum={false}
        style={{
          x: isMaximized ? 0 : position.x,
          y: isMaximized ? 0 : position.y,
          height: isMaximized ? 'calc(100vh - 42px)' : 'auto',
          width: isMaximized ? '100vw' : undefined,
          position: isMaximized ? 'fixed' : 'relative',
          top: isMaximized ? 0 : undefined,
          left: isMaximized ? 0 : undefined,
          right: isMaximized ? 0 : undefined,
          bottom: isMaximized ? 'auto' : undefined,
          margin: 0,
          borderRadius: isMaximized ? 0 : undefined,
          transform: isMaximized ? 'none !important' : undefined,
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{
          opacity: 1,
          scale: 1,
          x: isMaximized ? 0 : position.x,
          y: isMaximized ? 0 : position.y,
        }}
        transition={{
          duration: 0.15,
          ease: [0.4, 0.0, 0.2, 1],
        }}
        onDragStart={handleWindowClick}
        onDragEnd={(e, info) => {
          // Update position state after dragging ends
          if (!isMaximized) {
            const newPosition = {
              x: position.x + info.offset.x,
              y: position.y + info.offset.y,
            };
            // Ensure the new position is within bounds
            setPosition(ensurePositionWithinBounds(newPosition));
          }
        }}
      >
        <AlertHeader
          $color={color}
          onPointerDown={(e) => {
            // Only start drag if not clicking on window controls
            const target = e.target as HTMLElement;
            if (!target.closest('.window-controls') && !isMaximized) {
              dragControls.start(e);
            }
          }}
        >
          <h2>
            <span className="window-icon">{icon}</span>
            {header}
          </h2>
          <WindowControls className="window-controls">
            <ControlButton size="small" onClick={handleMinimize}>
              <MinimizeIcon fontSize="small" />
            </ControlButton>
            <ControlButton size="small" onClick={handleMaximize}>
              <CropSquareIcon fontSize="small" />
            </ControlButton>
            {onClose && (
              <ControlButton
                className="close-button"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
              >
                <CloseIcon fontSize="small" />
              </ControlButton>
            )}
          </WindowControls>
        </AlertHeader>

        <Content id="modal-content" $isMaximized={isMaximized}>
          {children}
        </Content>
      </Box>
    </Container>
  );
};

export default Modal;
